import type { MouseEvent } from "react";
import { BarChart3, Network } from "lucide-react";
import {
  buildDashboardGraphPayload,
  buildDashboardAnalyticsPayload,
  openDashboardGraphQuickLink,
  openDashboardAnalyticsQuickLink,
  type DashboardEntityKind,
} from "../../../lib/cloudEntityQuickLinks";

interface CloudEntityQuickLinksProps {
  cloudProvider: string;
  entityKind: DashboardEntityKind;
  entity: any;
  className?: string;
}

export const CloudEntityQuickLinks = ({
  cloudProvider,
  entityKind,
  entity,
  className = "",
}: CloudEntityQuickLinksProps) => {
  const analyticsPayload = buildDashboardAnalyticsPayload({
    cloudProvider,
    entityKind,
    entity,
  });
  const graphPayload = buildDashboardGraphPayload({
    cloudProvider,
    entityKind,
    entity,
  });

  const handleAnalyticsClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!analyticsPayload) {
      return;
    }

    openDashboardAnalyticsQuickLink(analyticsPayload);
  };

  const handleGraphClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!graphPayload) {
      return;
    }

    openDashboardGraphQuickLink(graphPayload);
  };

  const buttonBaseClassName =
    "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-semibold transition-all duration-200";
  const analyticsHoverText = analyticsPayload
    ? "Open this entity in CloudEntityAnalytics in a new tab."
    : "Analytics quick link is unavailable until full entity metadata is present.";
  const graphHoverText = graphPayload
    ? "Open this entity directly in Cloud Analytics graph in a new tab."
    : "Graph quick link is unavailable until the entity has enough cloud context to locate a graph node.";

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      <button
        type="button"
        onClick={handleAnalyticsClick}
        aria-disabled={!analyticsPayload}
        aria-label={analyticsHoverText}
        title={analyticsHoverText}
        className={`${buttonBaseClassName} ${
          analyticsPayload
            ? "border-sky-500/25 bg-gradient-to-r from-sky-500/10 via-cyan-500/10 to-primary/10 text-foreground hover:border-sky-500/40 hover:shadow-[0_8px_20px_rgba(14,165,233,0.12)]"
            : "cursor-not-allowed border-border/50 bg-muted/30 text-muted-foreground"
        }`}
      >
        <BarChart3 className="w-3.5 h-3.5 text-sky-500" />
        Open Analytics
      </button>

      <button
        type="button"
        onClick={handleGraphClick}
        aria-disabled={!graphPayload}
        aria-label={graphHoverText}
        title={graphHoverText}
        className={`${buttonBaseClassName} ${
          graphPayload
            ? "border-emerald-500/25 bg-gradient-to-r from-emerald-500/10 via-cyan-500/10 to-sky-500/10 text-foreground hover:border-emerald-500/40 hover:shadow-[0_8px_20px_rgba(16,185,129,0.14)]"
            : "cursor-not-allowed border-border/50 bg-card/70 text-muted-foreground"
        }`}
      >
        <Network
          className={`w-3.5 h-3.5 ${
            graphPayload ? "text-emerald-500" : "text-sky-500/80"
          }`}
        />
        Open Graph
      </button>
    </div>
  );
};
